import React, { useCallback, useEffect, useRef } from 'react';
import type { AdMeshLinkTrackerProps } from '../types/index';
import { logger } from '../utils/logger';

/**
 * AdMeshLinkTracker Component
 * 
 * Wraps content with click tracking via AdMesh click URLs.
 * 
 * Note: View/exposure tracking is handled separately by AdMeshViewabilityTracker component.
 * This component only handles click navigation to the admeshLink (which contains click tracking).
 * 
 * The admeshLink prop should be the click URL from the recommendation, which already includes
 * all necessary tracking parameters and will redirect to the merchant URL.
 */
export const AdMeshLinkTracker: React.FC<AdMeshLinkTrackerProps> = ({
  recommendationId: _recommendationId,
  admeshLink,
  productId: _productId,
  children,
  trackingData: _trackingData,
  className,
  style
}) => {
  const elementRef = useRef<HTMLDivElement>(null);

  // Ensure all child links open in new tab
  useEffect(() => {
    if (!elementRef.current) return;

    // Find all <a> tags within this component and ensure they open in new tab
    const links = elementRef.current.querySelectorAll('a');
    links.forEach((link) => {
      // Only set if not already set or if it's not _blank
      if (!link.hasAttribute('target') || link.getAttribute('target') !== '_blank') {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }, [children]); // Re-run when children change

  const handleClick = useCallback((event: React.MouseEvent) => {
    // The admeshLink already contains the click tracking URL from the backend
    // Navigating to it will automatically fire the click tracking pixel and redirect to merchant
    
    // If the children contain a link, ensure it opens in new tab
    // Otherwise, navigate programmatically
    const target = event.target as HTMLElement;
    const link = target.closest('a');

    if (!link) {
      // No link found, navigate programmatically to the click URL
      // The click URL will handle tracking and redirect
      if (admeshLink) {
        window.open(admeshLink, '_blank', 'noopener,noreferrer');
      } else {
        logger.warn('[AdMeshLinkTracker] No admeshLink provided for click tracking');
      }
    } else {
      // Link found - ensure it opens in new tab
      // If the link href is not set or is different from admeshLink, update it
      if (!link.href || link.href === '#' || link.href === '') {
        if (admeshLink) {
          link.href = admeshLink;
        }
      }
      if (!link.hasAttribute('target') || link.getAttribute('target') !== '_blank') {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
      // Let the browser handle navigation naturally (click URL will track and redirect)
    }
  }, [admeshLink]);

  return (
    <div
      ref={elementRef}
      className={className}
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        ...style
      }}
    >
      {children}
    </div>
  );
};

AdMeshLinkTracker.displayName = 'AdMeshLinkTracker';
